import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";

import useAuth from "../../hooks/useAuth";

const BASE_URL = process.env.REACT_APP_DOCUMENTS_API_URL || "http://164.52.203.201:8024/api/v1";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`job-doc-tabpanel-${index}`}
      aria-labelledby={`job-doc-tab-${index}`}
      {...other}
      style={{ minHeight: "300px" }}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const JobDocumentConfig = () => {
  const { user, organisationId, customerId } = useAuth();
  const orgId = organisationId || user?.org_id || user?.organisation_id || 10;
  const custId = customerId || user?.customer_id || user?.cust_id || 101;

  const [tabValue, setTabValue] = useState(0);
  const [masterDocs, setMasterDocs] = useState({}); // { BLUE_COLLAR: [], WHITE_COLLAR: [] }
  const [configuredDocs, setConfiguredDocs] = useState({ BLUE_COLLAR: [], WHITE_COLLAR: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Add Document Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Custom Document Dialog state
  const [customDocDialogOpen, setCustomDocDialogOpen] = useState(false);
  const [customDocName, setCustomDocName] = useState("");
  const [creatingCustom, setCreatingCustom] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      // 1. Fetch Master List
      console.log("Fetching master documents from:", `${BASE_URL}/document-requirements`);
      const masterRes = await axios.get(`${BASE_URL}/document-requirements`);
      console.log("Master Response:", masterRes.data);

      const masterMap = {};
      let hTypes = null;
      
      if (masterRes.data?.hiring_types) {
        hTypes = masterRes.data.hiring_types;
      } else if (masterRes.data?.data?.hiring_types) {
        hTypes = masterRes.data.data.hiring_types;
      } else if (Array.isArray(masterRes.data)) {
        hTypes = masterRes.data;
      } else if (Array.isArray(masterRes.data?.data)) {
        hTypes = masterRes.data.data;
      }

      if (hTypes) {
        hTypes.forEach((ht) => {
          masterMap[ht.hiring_type] = ht.documents || [];
        });
      } else {
        console.warn("Could not find hiring_types array in response", masterRes.data);
      }
      setMasterDocs(masterMap);
      console.log("Processed Master Map:", masterMap);

      // 2. Fetch Customer's Configured List
      console.log("Fetching customer documents from:", `${BASE_URL}/customer-document-requirements`);
      const customerRes = await axios.get(
        `${BASE_URL}/customer-document-requirements?org_id=${orgId}&customer_id=${custId}&page_no=1&max_rows=100`
      );
      console.log("Customer Response:", customerRes.data);
      
      const configMap = { BLUE_COLLAR: [], WHITE_COLLAR: [] };
      if (customerRes.data && customerRes.data.documents) {
        customerRes.data.documents.forEach((doc) => {
          if (doc.is_active) {
            if (!configMap[doc.hiring_type]) {
              configMap[doc.hiring_type] = [];
            }
            configMap[doc.hiring_type].push(doc);
          }
        });
      }
      setConfiguredDocs(configMap);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      showSnackbar("Failed to load document configuration.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const currentHiringType = tabValue === 0 ? "BLUE_COLLAR" : "WHITE_COLLAR";

  const handleOpenAddMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAddMenu = () => {
    setAnchorEl(null);
  };

  const handleAddDocument = (masterDoc) => {
    setConfiguredDocs((prev) => {
      const typeList = prev[currentHiringType] ? [...prev[currentHiringType]] : [];
      // Add with default is_mandatory = true
      typeList.push({
        document_requirement_id: masterDoc.document_requirement_id,
        hiring_type: currentHiringType,
        document_type: masterDoc.document_type,
        display_name: masterDoc.display_name,
        is_mandatory: true,
        is_active: true,
        ...(masterDoc.is_custom ? { is_custom: true } : {})
      });
      return { ...prev, [currentHiringType]: typeList };
    });
    handleCloseAddMenu();
  };

  const handleOpenCustomDialog = () => {
    handleCloseAddMenu();
    setCustomDocDialogOpen(true);
  };

  const handleCloseCustomDialog = () => {
    setCustomDocDialogOpen(false);
    setCustomDocName("");
  };

  const handleCreateCustomDocument = () => {
    if (!customDocName.trim()) return;
    
    // We create a temporary ID for the React list, but flag it as custom
    // so we don't send the fake ID to the backend.
    const tempId = `custom-${Date.now()}`;
    
    const newDoc = {
      document_requirement_id: tempId,
      document_type: "CUSTOM",
      display_name: customDocName.trim(),
      is_custom: true // Important flag for handleSave
    };
    
    // Add it to our local state
    handleAddDocument(newDoc);
    showSnackbar("Custom document added to the list! Don't forget to save changes.");
    handleCloseCustomDialog();
  };

  const handleToggleMandatory = (docReqId) => {
    setConfiguredDocs((prev) => {
      const typeList = prev[currentHiringType].map((doc) => {
        if (doc.document_requirement_id === docReqId) {
          return { ...doc, is_mandatory: !doc.is_mandatory };
        }
        return doc;
      });
      return { ...prev, [currentHiringType]: typeList };
    });
  };

  const handleRemoveDocument = (docReqId) => {
    setConfiguredDocs((prev) => {
      const typeList = prev[currentHiringType].filter(
        (doc) => doc.document_requirement_id !== docReqId
      );
      return { ...prev, [currentHiringType]: typeList };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build payload matching the required structure
      const hiringTypesPayload = Object.keys(configuredDocs).map((hType) => {
        const standardDocs = [];
        const customDocs = [];

        configuredDocs[hType].forEach((doc) => {
          if (doc.is_custom) {
            customDocs.push({
              document_type: doc.display_name.toUpperCase().replace(/\s+/g, '_'),
              display_name: doc.display_name,
              is_mandatory: doc.is_mandatory,
            });
          } else {
            standardDocs.push({
              document_requirement_id: doc.document_requirement_id,
              is_mandatory: doc.is_mandatory,
            });
          }
        });

        return {
          hiring_type: hType,
          documents: standardDocs,
          others: customDocs,
        };
      });

      const payload = {
        org_id: orgId,
        customer_id: custId,
        hiring_types: hiringTypesPayload
      };

      await axios.post(`${BASE_URL}/customer-document-requirements`, payload);
      
      showSnackbar("Documents saved successfully!");
      
      // Refresh to get latest DB state
      await fetchConfig();
    } catch (error) {
      console.error("Failed to save:", error);
      showSnackbar(
        error?.response?.data?.detail || "Failed to save configuration.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  // Filter master docs to only show ones that aren't already added
  const availableMasterDocs = (masterDocs[currentHiringType] || []).filter(
    (mDoc) =>
      !configuredDocs[currentHiringType]?.some(
        (cDoc) => cDoc.document_requirement_id === mDoc.document_requirement_id
      )
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderTable = (hType) => {
    const docs = configuredDocs[hType] || [];

    if (docs.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No documents configured. Click "Add Document" to add some.
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #E5E7EB" }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Document Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "150px" }}>Is Mandatory?</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "100px", textAlign: "right" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docs.map((row) => (
              <TableRow key={row.document_requirement_id}>
                <TableCell>{row.display_name}</TableCell>
                <TableCell>
                  <Switch
                    checked={row.is_mandatory}
                    onChange={() => handleToggleMandatory(row.document_requirement_id)}
                    color="primary"
                  />
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveDocument(row.document_requirement_id)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ flex: 1 }}>
      <Paper elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider', px: 2, pt: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600} mb={1}>
              Job Document Config
            </Typography>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="job document config tabs"
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#1E40AF",
                },
              }}
            >
              <Tab
                label="Blue Collar"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  "&.Mui-selected": { color: "#1E40AF" },
                }}
              />
              <Tab
                label="White Collar"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  "&.Mui-selected": { color: "#1E40AF" },
                }}
              />
            </Tabs>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddMenu}
            >
              Add Document
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ backgroundColor: "#1E40AF", "&:hover": { backgroundColor: "#1e3a8a" } }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>

        <CustomTabPanel value={tabValue} index={0}>
          {renderTable("BLUE_COLLAR")}
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          {renderTable("WHITE_COLLAR")}
        </CustomTabPanel>
      </Paper>

      {/* Add Document Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseAddMenu}
        PaperProps={{ style: { maxHeight: 400, width: '250px' } }}
      >
        <MenuItem onClick={handleOpenCustomDialog} sx={{ color: '#1E40AF', fontWeight: 600 }}>
          <AddIcon fontSize="small" sx={{ mr: 1 }} /> Create Custom Document
        </MenuItem>
        <Divider />
        
        {availableMasterDocs.length === 0 ? (
          <MenuItem disabled>No available documents</MenuItem>
        ) : (
          availableMasterDocs.map((doc) => (
            <MenuItem key={doc.document_requirement_id} onClick={() => handleAddDocument(doc)}>
              {doc.display_name}
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Custom Document Dialog */}
      <Dialog open={customDocDialogOpen} onClose={handleCloseCustomDialog} fullWidth maxWidth="sm">
        <DialogTitle>Create Custom Document</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This will create a brand new document requirement for {currentHiringType === "BLUE_COLLAR" ? "Blue Collar" : "White Collar"} jobs.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Document Name"
            type="text"
            fullWidth
            variant="outlined"
            value={customDocName}
            onChange={(e) => setCustomDocName(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleCloseCustomDialog} disabled={creatingCustom}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateCustomDocument} 
            variant="contained" 
            disabled={!customDocName.trim() || creatingCustom}
            startIcon={creatingCustom ? <CircularProgress size={20} /> : null}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobDocumentConfig;
